import type { TaggedRoutineStepVariables } from '../RoutineStepVariables';

import type { FileNamePart } from './captureConfig';
import type { CapturingStepKind } from './captureFileName';
import {
  fileNamePartTexts,
  generatedCaptureName,
  NAME_VALUE_KEYS,
  nameValueKey,
} from './captureFileName';
import type { CaptureFrameArgs } from './captureFrameToStorage';
import { formatCaptureTimestamp } from './captureUtils';

/** A capture's file name parts, read for one play. */
export interface FileNameParts {
  /** Each part's text, in order. */
  parts: string[];
  /** The step number, step name and variables among them, by `nameValueKey`. */
  nameValues: Record<string, string>;
}

/** How one play names its frame, as `captureFrameToStorage` takes it. */
export type FrameName = Required<Pick<CaptureFrameArgs, 'captureTimestamp'>> &
  Pick<CaptureFrameArgs, 'fileName' | 'nameValues'>;

function nameText(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

/** A deleted variable or step fails the step; validation reports it first. */
export function readFileNameParts(
  fileName: FileNamePart[] | undefined,
  step: {
    description?: string;
    getStepPosition(): number;
    routineFrame: {
      getVariable(id: string): TaggedRoutineStepVariables;
      getVariableByName(
        name: string,
        stepKind?: TaggedRoutineStepVariables['stepKind'],
      ): { variable: TaggedRoutineStepVariables } | null;
    };
  },
): FileNameParts {
  const nameValues: Record<string, string> = {};

  const readVariable = (id: string): TaggedRoutineStepVariables => {
    try {
      return step.routineFrame.getVariable(id);
    } catch {
      throw new Error(
        `the file name uses a variable or step that no longer exists (${id})`,
      );
    }
  };

  const nameValue = (name: string, value: string): string => {
    const key = nameValueKey(name);

    if (key) {
      nameValues[key] = value;
    }

    return value;
  };

  const parts = fileNamePartTexts(fileName ?? [], {
    stepNumber: () => {
      const stepNumber = String(step.getStepPosition());

      nameValues[NAME_VALUE_KEYS.stepNumber] = stepNumber;

      return stepNumber;
    },
    stepName: () => {
      const stepName = step.description ?? '';

      nameValues[NAME_VALUE_KEYS.stepName] = stepName;

      return stepName;
    },
    variable: (variableID) => {
      const variable = readVariable(variableID);

      if (variable.stepKind !== 'EnvironmentVariable') {
        throw new Error(
          `the file name uses ${variableID}, which is not a routine variable`,
        );
      }

      return nameValue(variable.name, nameText(variable.value));
    },
    stepOutput: (stepID, name) => {
      const outputs = readVariable(stepID) as Record<string, unknown>;

      return nameValue(name, nameText(outputs[name]));
    },
    textVariable: (name) => {
      const variable = step.routineFrame.getVariableByName(
        name,
        'EnvironmentVariable',
      )?.variable;

      if (variable?.stepKind !== 'EnvironmentVariable') {
        throw new Error(
          `the file name's text uses {${name}}, which is not a routine variable`,
        );
      }

      return nameValue(name, nameText(variable.value));
    },
    text: (literal) => literal,
  });

  return { parts, nameValues };
}

/**
 * `frameName.fileName` is set only when there are parts, so a step with none
 * keeps the names its captures have always had.
 */
export function nameFrame(args: {
  stepKind: CapturingStepKind;
  skillId?: string;
  fileNameParts: FileNameParts;
}): { name: string; frameName: FrameName } {
  const { stepKind, skillId, fileNameParts } = args;

  const captureTimestamp = formatCaptureTimestamp(new Date());

  const name = generatedCaptureName({
    stepKind,
    skillId,
    timestamp: captureTimestamp,
    parts: fileNameParts.parts,
  });

  return {
    name,
    frameName: {
      captureTimestamp,
      fileName: fileNameParts.parts.length > 0 ? name : undefined,
      nameValues:
        Object.keys(fileNameParts.nameValues).length > 0
          ? fileNameParts.nameValues
          : undefined,
    },
  };
}
